import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { ClipboardList, Search, FileCheck, CheckCircle } from 'lucide-react';

export default function Process() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        duration: 0.8
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const steps = [
    {
      icon: ClipboardList,
      step: "1",
      title: "Initial Consultation",
      description: "We start by understanding your business goals, property details, and financial objectives to create a customized financing strategy."
    },
    {
      icon: Search,
      step: "2",
      title: "Property & Financial Assessment",
      description: "Comprehensive evaluation of your property's value, income potential, and overall financial health to determine optimal lending solutions."
    },
    {
      icon: FileCheck,
      step: "3",
      title: "Deal Structuring & Lender Matching",
      description: "We design the best loan terms and match you with the most suitable lenders from our extensive network of financial institutions."
    },
    {
      icon: CheckCircle,
      step: "4",
      title: "Funding & Closing",
      description: "We guide you through the entire closing process, ensuring smooth documentation and timely fund disbursement for your project."
    }
  ];

  return (
    <section id="process" ref={sectionRef} className="py-24 bg-slate-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900"></div>
      
      <motion.div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Our <span className="text-emerald-400">Process</span>
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            A streamlined approach to securing your commercial mortgage financing
          </p>
        </motion.div>

        <motion.div variants={containerVariants} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group text-center p-8 bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10 hover:border-emerald-400/30 transition-all duration-500"
                whileHover={{ 
                  scale: 1.05,
                  y: -5,
                  transition: { duration: 0.3 }
                }}
              >
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {step.step}
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold text-white mb-4">
                  {step.title}
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div 
          variants={itemVariants}
          className="text-center mt-12"
        >
          <motion.button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 bg-emerald-500 text-white rounded-lg font-semibold hover:bg-emerald-600 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-emerald-500/50"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Your Application
          </motion.button>
        </motion.div>
      </motion.div>
    </section>
  );
}